import { useQuery, useMutation } from '@tanstack/react-query';
import axios from 'axios';
import { APPMAKER_GQL_URL } from '@appmaker/core/api/core';
import { useMemo } from 'react';
import { getProjectData } from '@appmaker/core/store/project';

/**
 * GraphQL query for extension installs
 */
const EXTENSION_INSTALLS_QUERY = `
  query ($where: ExtensionInstallWhereInput!) {
    extensionInstalls(where: $where) {
      id,
      settings
    }
  }
`;

/**
 * GraphQL mutation for updating extension installs
 */
const UPDATE_EXTENSION_INSTALL_MUTATION = `
  mutation ($where: ExtensionInstallWhereUniqueInput!, $data: ExtensionInstallUpdateInput!) {
    updateExtensionInstall(where: $where, data: $data) {
      id
      settings
      __typename
    }
  }
`;

/**
 * @typedef {Object} ExtensionInstallWhereInput
 * @property {string} [projectId] - Project ID filter
 * @property {string} [extensionId] - Extension ID filter
 */

/**
 * @typedef {Object} ExtensionInstall
 * @property {string} id - Extension install ID
 * @property {Object} settings - Extension settings object
 */

// Helper function to get project ID from project data
const getProjectId = () => {
    const { projectData } = getProjectData();
    return projectData?.id || projectData?.project_id;
};

/**
 * GraphQL fetch function for extension installs
 * @param {string} projectId - Project ID to be sent in headers
 * @param {Object} variables - GraphQL query variables
 * @param {AbortSignal} signal - Request cancellation signal
 * @returns {Promise} GraphQL response data
 */
const fetchExtensionInstalls = async (projectId, variables, signal) => {
    const response = await axios({
        method: 'POST',
        url: APPMAKER_GQL_URL,
        headers: {
            'Content-Type': 'application/json',
            'x-appmaker-project-id': projectId
        },
        data: {
            query: EXTENSION_INSTALLS_QUERY,
            variables
        },
        withCredentials: true,
        signal
    });

    if (response.data.errors) {
        throw new Error(response.data.errors[0]?.message || 'GraphQL query failed');
    }

    return response.data.data?.extensionInstalls || [];
};

/**
 * Fetches extension installs using GraphQL.
 * @param {string} projectId - The ID of the project.
 * @param {ExtensionInstallWhereInput} whereInput - GraphQL where input for filtering.
 * @param {object} options - React Query options.
 * @returns {Object} React Query result with extension installs data
 * @example
 * const { data, isLoading, error } = useExtensionInstalls('project-123', {
 *   extensionId: 'some-extension-id'
 * }, {
 *   onSuccess: (data) => console.log('Extension installs loaded:', data),
 *   onError: (error) => console.error('Failed to load extension installs:', error)
 * });
 */
export const useExtensionInstalls = (projectId, whereInput = {}, options = {}) => {
    const variables = useMemo(() => ({ where: whereInput }), [whereInput]);
    
    return useQuery(
        ['extensionInstalls', projectId, variables],
        ({ signal }) => fetchExtensionInstalls(projectId, variables, signal),
        {
            enabled: !!projectId,
            staleTime: 5 * 60 * 1000, // 5 minutes
            ...options
        }
    );
};

/**
 * Custom hook that automatically uses the current project ID for extension installs.
 * @param {ExtensionInstallWhereInput} whereInput - GraphQL where input for filtering.
 * @param {object} options - React Query options.
 * @returns {Object} React Query result with extension installs data
 * @example
 * const { data, isLoading, error } = useCurrentProjectExtensionInstalls({
 *   extensionId: 'some-extension-id'
 * });
 */
export const useCurrentProjectExtensionInstalls = (whereInput = {}, options = {}) => {
    const projectId = getProjectId();
    return useExtensionInstalls(projectId, whereInput, { enabled: !!projectId, ...options });
};

/**
 * GraphQL mutation function for updating extension installs
 * @param {string} projectId - Project ID to be sent in headers
 * @param {Object} variables - GraphQL mutation variables
 * @param {AbortSignal} signal - Request cancellation signal
 * @returns {Promise} GraphQL response data
 */
const updateExtensionInstall = async (projectId, variables, signal) => {
    const response = await axios({
        method: 'POST',
        url: APPMAKER_GQL_URL,
        headers: {
            'Content-Type': 'application/json',
            'x-appmaker-project-id': projectId
        },
        data: {
            query: UPDATE_EXTENSION_INSTALL_MUTATION,
            variables
        },
        withCredentials: true,
        signal
    });

    if (response.data.errors) {
        throw new Error(response.data.errors[0]?.message || 'GraphQL mutation failed');
    }

    return response.data.data?.updateExtensionInstall;
};

/**
 * Custom hook for updating extension installs using GraphQL mutation.
 * @param {object} options - React Query mutation options.
 * @returns {Object} React Query mutation result
 * @example
 * const updateMutation = useUpdateExtensionInstall({
 *   onSuccess: (data) => console.log('Extension install updated:', data),
 *   onError: (error) => console.error('Failed to update extension install:', error)
 * });
 * 
 * // Usage:
 * updateMutation.mutate({
 *   where: { id: 'extension-install-id' },
 *   data: { settings: { ... } }
 * });
 */
export const useUpdateExtensionInstall = (options = {}) => {
    const projectId = getProjectId();
    
    return useMutation(
        (variables) => updateExtensionInstall(projectId, variables),
        {
            ...options,
            onError: (error) => {
                console.error('Extension install update failed:', error);
                options.onError?.(error);
            }
        }
    );
};